(() => {
  /* global Shopify */
  const NAME = "product-sticky-atc";

  if (customElements.get(NAME)) return;

  class ProductStickyAtc extends HTMLElement {
    #observer = null;
    #abortController = null;
    #submit = null;
    #sizeNode = null;
    #image = null;
    #earlyAccessPrices = null;

    connectedCallback() {
      this.#submit = this.querySelector("[data-sticky-atc-submit]");
      this.#sizeNode = this.querySelector("[data-sticky-atc-size]");
      this.#image = this.querySelector(".product-sticky-atc__image");
      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      document.addEventListener("variant:change", this.#onVariantChange, {
        signal,
      });
      this.addEventListener("click", this.#onClick, { signal });

      this.#watchBuyButtons();
      this.#syncNotifyState(this.#submit ? !this.#submit.disabled : true);
      this.#syncPreorderLabel();
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#observer?.disconnect();
      this.#observer = null;
    }

    /**
     * Reveal once the real buy row has scrolled off the top. Watching the row
     * rather than a scroll offset keeps the bar correct whatever the form's
     * height ends up being — and rather than the add to bag button itself,
     * which is display:none while the out of stock notify CTA is showing and
     * would report a zero rect that never reads as scrolled past.
     */
    #watchBuyButtons() {
      const main =
        document.querySelector(".product-buy-buttons__row") ||
        document.querySelector(".product-buy-buttons [data-add-to-cart]");

      if (!main) return;

      this.#observer = new IntersectionObserver(
        ([entry]) => {
          const scrolledPast =
            !entry.isIntersecting && entry.boundingClientRect.top < 0;

          this.#setVisible(scrolledPast);
        },
        { threshold: 0 },
      );

      this.#observer.observe(main);
    }

    #setVisible(visible) {
      if (visible === !this.hidden) return;

      if (!visible) {
        this.hidden = true;
        return;
      }

      this.hidden = false;

      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

      // Paint the off-screen position before releasing it, or the slide up is
      // skipped and the bar simply appears.
      this.dataset.stickyAtcEntering = "";
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          delete this.dataset.stickyAtcEntering;
        });
      });
    }

    #onVariantChange = (event) => {
      const variant = event.detail?.variant;

      if (!variant) return;

      if (this.#sizeNode) {
        const size = this.#sizeFromVariant(event.detail);

        if (size) this.#sizeNode.textContent = size;
      }

      this.#updateImage(variant);

      if (!this.#submit) return;

      const addLabel = this.#submit.dataset.addToBagLabel || "Add to bag";
      const soldOutLabel = this.#submit.dataset.soldOutLabel || "Sold out";

      if (this.#syncNotifyState(variant.available)) return;

      const preorderLabel = this.#preorderLabel();

      this.#submit.disabled = !variant.available;
      this.#submit.textContent = variant.available
        ? `${this.#formatPrice(this.#earlyAccessPrice(variant))} | ${preorderLabel || addLabel}`
        : soldOutLabel;
    };

    #earlyAccessPrice(variant) {
      if (this.#earlyAccessPrices == null) {
        const node = document.querySelector("[data-early-access-prices]");
        this.#earlyAccessPrices = {};

        if (node) {
          try {
            this.#earlyAccessPrices = JSON.parse(node.textContent);
          } catch {
            this.#earlyAccessPrices = {};
          }
        }
      }

      const override = this.#earlyAccessPrices[String(variant.id)];

      if (override == null) return variant.price;

      return override;
    }

    /**
     * The main buy button owns the pre-order decision, so read it back rather
     * than repeating the metafield logic here. Null means this variant is an
     * ordinary add to bag.
     */
    #preorderLabel() {
      const main = document.querySelector(
        ".product-buy-buttons [data-add-to-cart]",
      );

      if (main?.dataset.preorderActive !== "true") return null;

      return main.dataset.preorderLabel || "Pre-order";
    }

    /**
     * First paint: Liquid rendered the bar before the buy buttons resolved,
     * so a pre-order variant would sit here reading "Add to bag" until the
     * shopper touched the variant picker.
     */
    #syncPreorderLabel() {
      if (!this.#submit) return;

      const label = this.#preorderLabel();

      if (!label) return;

      const price = this.#submit.dataset.priceLabel || "";

      this.#submit.textContent = price ? `${price} | ${label}` : label;
    }

    /**
     * The bar mirrors the main buy buttons, so when those swap their sold out
     * button for a Klaviyo notify CTA the sticky button has to follow. Copying
     * the trigger's data attributes is enough — the buy buttons' own delegated
     * click handler opens the form.
     */
    #syncNotifyState(available) {
      if (!this.#submit) return false;

      const main = document.querySelector(
        ".product-buy-buttons [data-notify-trigger]",
      );
      const useNotify = !available && main != null && !main.hidden;

      if (useNotify) {
        this.#submit.type = "button";
        this.#submit.disabled = false;
        this.#submit.textContent = main.textContent.trim();
        this.#submit.dataset.notifyTrigger = "";
        this.#submit.dataset.klaviyoFormId = main.dataset.klaviyoFormId || "";
        this.#submit.dataset.variantId = main.dataset.variantId || "";
        return true;
      }

      this.#submit.type = "submit";
      delete this.#submit.dataset.notifyTrigger;
      delete this.#submit.dataset.klaviyoFormId;
      delete this.#submit.dataset.variantId;

      return false;
    }

    /**
     * Variants that carry their own image swap the thumbnail. srcset has to go
     * with it — leaving the old one in place would win over the new src.
     * Variants without an image keep whatever is already showing.
     */
    #updateImage(variant) {
      const source = variant?.featured_image?.src;

      if (!this.#image || !source) return;

      const sized = (width) => {
        try {
          const url = new URL(source, window.location.origin);
          url.searchParams.set("width", String(width));
          return url.toString();
        } catch {
          return source;
        }
      };

      this.#image.src = sized(114);
      this.#image.srcset = `${sized(57)} 57w, ${sized(114)} 114w`;
    }

    #sizeFromVariant(detail) {
      const options = detail?.product?.options;
      const variant = detail?.variant;

      if (!Array.isArray(options) || !variant) return null;

      const index = options.findIndex(
        (name) => String(name || "").toLowerCase() === "size",
      );

      if (index < 0) return null;

      return variant[`option${index + 1}`] || variant.options?.[index] || null;
    }

    #formatPrice(cents) {
      const format =
        window.theme?.moneyFormat ||
        (typeof Shopify !== "undefined" ? Shopify.money_format : null);

      if (
        typeof Shopify !== "undefined" &&
        typeof Shopify.formatMoney === "function"
      ) {
        return Shopify.formatMoney(cents, format);
      }

      return (Number(cents) / 100).toFixed(2);
    }

    #onClick = (event) => {
      if (!event.target.closest("[data-sticky-atc-edit]")) return;

      event.preventDefault();

      const picker = document.querySelector(
        ".product-variant-picker__group--size, .product-variant-picker",
      );

      picker?.scrollIntoView({ behavior: "smooth", block: "center" });
      picker
        ?.querySelector("[data-size-input]")
        ?.focus({ preventScroll: true });
    };
  }

  customElements.define(NAME, ProductStickyAtc);
})();
